import React, { useEffect, useRef, useState } from "react";
import { Camera, ScanLine, X, Loader2, Sparkles } from "lucide-react";
import { LiveScanner } from "./LiveScanner";
import { extractProductFromImage, resizeFileToJpegDataUrl } from "../lib/extractProduct";

/**
 * Smart-capture controls for the Product Request sheet.
 *
 *   1. "Scan barcode (live)" — opens the existing LiveScanner overlay.
 *      First detection fills `barcode` and closes itself.
 *   2. "Capture from photo" — opens the OS camera/gallery picker. The
 *      delegated `extractProductFromImage` helper runs ZXing locally,
 *      hits `/api/product-requests/extract` for product_name + brand,
 *      and fires the result-specific toast (no silent successes).
 *
 *  All field updates flow through `onApply({barcode?, product_name?, brand?, source})`
 *  so the parent owns the editable form. Values remain editable after detection.
 */
export function SmartCaptureBlock({ initialBarcode = "", onApply, disabled = false }) {
  const [liveOpen, setLiveOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastSource, setLastSource] = useState(null);
  const [lastSummary, setLastSummary] = useState(null);
  const fileRef = useRef(null);
  // initialBarcode is reserved for future "compare detected vs known"
  // hints; intentionally read here to silence linter.
  void initialBarcode;

  const openLive = () => { if (!disabled) setLiveOpen(true); };
  const openCameraPicker = () => {
    if (disabled || busy || !fileRef.current) return;
    fileRef.current.value = "";
    fileRef.current.click();
  };

  const onLiveDetected = (code) => {
    if (!code) return;
    setLiveOpen(false);
    setLastSource("barcode_scan");
    setLastSummary(`Barcode ${code}`);
    onApply({ barcode: String(code), source: "barcode_scan" });
  };

  const onFile = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setBusy(true);
    try {
      const { dataUrl } = await resizeFileToJpegDataUrl(f);
      const res = await extractProductFromImage(dataUrl, onApply);
      if (res.appliedAny) {
        setLastSource(res.source || "photo_ocr");
        setLastSummary(res.summary || null);
      } else {
        setLastSource(null);
        setLastSummary(null);
      }
    } catch {
      // toast already shown by helper or photo-corrupt fallback
      setLastSource(null);
      setLastSummary(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-dashed border-brand/60 rounded-sm p-3 bg-brand/5" data-testid="smart-capture-block">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={14} className="text-brand" strokeWidth={2.5} />
        <div className="font-display text-xs uppercase tracking-wider">Scan barcode / product label</div>
      </div>
      <p className="font-mono text-[10px] text-ink/55 leading-snug mb-3">
        Save typing — tap to scan a barcode live, or snap the label and we'll fill the fields.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={openLive}
          disabled={disabled || busy}
          className="h-12 inline-flex items-center justify-center gap-2 bg-ink text-white font-display text-xs uppercase tracking-wider rounded-sm active:scale-[0.98] disabled:opacity-50"
          data-testid="smart-capture-live-button"
        >
          <ScanLine size={16} strokeWidth={2.5} />
          Live scan
        </button>
        <button
          type="button"
          onClick={openCameraPicker}
          disabled={disabled || busy}
          className="h-12 inline-flex items-center justify-center gap-2 border-2 border-ink text-ink font-display text-xs uppercase tracking-wider rounded-sm active:scale-[0.98] active:bg-ink/5 disabled:opacity-50"
          data-testid="smart-capture-photo-button"
        >
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Camera size={16} strokeWidth={2.5} />}
          {busy ? "Reading…" : "From photo"}
        </button>
      </div>
      {lastSummary && (
        <div
          className="mt-2 font-mono text-[10px] uppercase tracking-wider text-shelf-fresh flex items-center gap-1.5"
          data-testid="smart-capture-result"
        >
          <span aria-hidden="true">{lastSource ? "✓" : ""}</span>
          <span>{lastSummary} · edit below before submitting</span>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFile}
        data-testid="smart-capture-input"
      />

      {liveOpen && (
        <LiveScannerOverlay onClose={() => setLiveOpen(false)} onDetected={onLiveDetected} />
      )}
    </div>
  );
}

function LiveScannerOverlay({ onClose, onDetected }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return (
    <div className="fixed inset-0 z-[60] bg-ink/95 flex flex-col" data-testid="smart-capture-live-overlay">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <div className="font-display text-sm uppercase tracking-wider">Live barcode scanner</div>
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 -mr-2 flex items-center justify-center rounded-full hover:bg-white/10"
          aria-label="Close live scanner"
          data-testid="smart-capture-live-close"
        >
          <X size={22} />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-sm aspect-square">
          <LiveScanner onDetected={onDetected} paused={false} className="w-full h-full" />
        </div>
      </div>
      <div className="px-6 pb-6 text-center text-white/70 font-mono text-[11px] tracking-wide">
        Point at a barcode — we'll fill the fields automatically.
      </div>
    </div>
  );
}

export default SmartCaptureBlock;
